import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, Mail, Phone, Calendar, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mld_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminLeads() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [consultations, setConsultations] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    document.title = "Espace privé — MEDERNACH LIONEL DESIGNER";
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setConsultations([]);
    setContacts([]);
  }, []);

  const loadLeads = useCallback(async (tk) => {
    setLoading(true);
    setError("");
    try {
      const headers = { Authorization: `Bearer ${tk}` };
      const [c, ct] = await Promise.all([
        axios.get(`${API}/consultation`, { headers }),
        axios.get(`${API}/contact`, { headers }),
      ]);
      setConsultations(c.data || []);
      setContacts(ct.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        setError("Session expirée. Merci de vous reconnecter.");
      } else {
        setError("Impossible de charger les données.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) loadLeads(token);
  }, [token, loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setPassword("");
      setToken(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Connexion impossible.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A1420] text-white flex items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm border border-[#D4AF37]/20 bg-[#0B1622] p-10"
        >
          <div className="flex flex-col items-center text-center mb-8">
            <Lock className="text-[#D4AF37] mb-4" size={28} />
            <h1 className="font-serif-lux text-2xl">Espace privé</h1>
            <p className="text-[#A0ABC0] text-sm mt-2">Accès réservé — MEDERNACH LIONEL DESIGNER</p>
          </div>
          <input
            type="password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            data-testid="admin-password-input"
            className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none transition-colors placeholder:text-[#A0ABC0]/60"
          />
          {error && (
            <p data-testid="admin-error" className="text-red-400 text-sm mt-4">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full mt-8 bg-[#D4AF37] text-[#0A1420] font-semibold hover:bg-[#E6C27A] transition-colors px-8 py-3.5 uppercase tracking-widest text-sm disabled:opacity-60"
          >
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div data-testid="admin-leads-page" className="min-h-screen bg-[#0A1420] text-white">
      <header className="border-b border-white/10 bg-[#0B1622]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-serif-lux text-xl gold-gradient-text">Mes prospects</h1>
            <p className="text-[#A0ABC0] text-xs mt-1">Espace privé — MEDERNACH LIONEL DESIGNER</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadLeads(token)}
              data-testid="admin-refresh"
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#A0ABC0] border border-white/15 hover:border-[#D4AF37] px-4 py-2.5 transition-colors"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualiser
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#0A1420] bg-[#D4AF37] hover:bg-[#E6C27A] px-4 py-2.5 transition-colors"
            >
              <LogOut size={14} /> Quitter
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-12 space-y-16">
        {error && <p className="text-red-400 text-sm">{error}</p>}

        {/* Consultations */}
        <section>
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="font-serif-lux text-2xl">Demandes de consultation</h2>
            <span className="text-[#D4AF37] text-sm">({consultations.length})</span>
          </div>
          {consultations.length === 0 ? (
            <p className="text-[#A0ABC0] text-sm">Aucune demande pour le moment.</p>
          ) : (
            <div className="space-y-4">
              {consultations.map((c) => (
                <div
                  key={c.id}
                  data-testid="consultation-row"
                  className="border border-[#D4AF37]/10 hover:border-[#D4AF37]/30 transition-colors bg-[#0B1622] p-6"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-white font-medium">{c.name}</p>
                    <span className="text-[#A0ABC0] text-xs flex items-center gap-1.5">
                      <Calendar size={13} /> {fmtDate(c.created_at)}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#A0ABC0]">
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-[#D4AF37]">
                      <Mail size={13} /> {c.email}
                    </a>
                    {c.phone && (
                      <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-[#D4AF37]">
                        <Phone size={13} /> {c.phone}
                      </a>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-3 text-xs">
                    <span className="border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-1">{c.project_type}</span>
                    {c.budget && <span className="border border-white/15 text-[#A0ABC0] px-3 py-1">{c.budget}</span>}
                    {c.preferred_date && (
                      <span className="border border-white/15 text-[#A0ABC0] px-3 py-1">{c.preferred_date}</span>
                    )}
                  </div>
                  {c.message && (
                    <p className="mt-4 text-[#e8ecf3] leading-relaxed whitespace-pre-wrap">{c.message}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Contact messages */}
        <section>
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="font-serif-lux text-2xl">Messages de contact</h2>
            <span className="text-[#D4AF37] text-sm">({contacts.length})</span>
          </div>
          {contacts.length === 0 ? (
            <p className="text-[#A0ABC0] text-sm">Aucun message pour le moment.</p>
          ) : (
            <div className="space-y-4">
              {contacts.map((m) => (
                <div
                  key={m.id}
                  data-testid="contact-row"
                  className="border border-[#D4AF37]/10 hover:border-[#D4AF37]/30 transition-colors bg-[#0B1622] p-6"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-white font-medium">{m.name}</p>
                    <span className="text-[#A0ABC0] text-xs flex items-center gap-1.5">
                      <Calendar size={13} /> {fmtDate(m.created_at)}
                    </span>
                  </div>
                  <a
                    href={`mailto:${m.email}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#A0ABC0] hover:text-[#D4AF37]"
                  >
                    <Mail size={13} /> {m.email}
                  </a>
                  {m.subject && <p className="mt-3 text-[#D4AF37] text-sm">{m.subject}</p>}
                  <p className="mt-3 text-[#e8ecf3] leading-relaxed whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
